import { Component, OnInit, inject, input } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { EntitySummaryType } from '@core/models/entities/entity-visualization-interface';
import { isNil, range } from '@core/utils/shared-utils';

import { TableColumn } from '@shared/components/table/table.model';
import { TooltipComponent } from '@shared/components/tooltip/tooltip.component';

import { EntitySummary } from '@features/components/entities/summary/entities-summary';
import { SummaryItem } from '@features/components/entities/summary/summary-item';

@Component({
  selector: 'app-table-summary',
  imports: [TooltipComponent],
  templateUrl: './table-summary.component.html'
})
export class TableSummaryComponent<T> implements OnInit {
  unusedRightColumns = input<number>(0);
  unusedLeftColumns = input<number>(0);
  tableColumnStyles = input<Record<string, string>>({});
  summaryItems = input<SummaryItem<T>[] | undefined>([]);
  columns = input<TableColumn<T>[]>([]);

  protected readonly summaryLabels: Record<string, string> = {};

  private readonly translate = inject(TranslateService);

  ngOnInit() {
    EntitySummary.SUMMARY_TYPES.forEach((type) => {
      this.summaryLabels[type] = this.translate.instant(`table.summary.${type}`);
    });
  }

  protected getSummaryForColumn(column: TableColumn<T>): SummaryItem<T> | undefined {
    const items = this.summaryItems();
    if (isNil(items)) return undefined;

    return items.find((item) => String(item.column.key) === String(column.key));
  }

  protected getPrimarySummaryValue(summaryItem: SummaryItem<T>): string {
    const summaryTypes = summaryItem.summaryTypes;
    if (!summaryTypes || summaryTypes.length === 0) return '-';

    const primaryType = summaryTypes[0];
    const value = summaryItem.getFormattedValueFor(primaryType);

    if (isNil(value)) return '-';

    return value;
  }

  protected getPrimarySummaryUnit(summaryItem: SummaryItem<T>): string | undefined {
    const primaryType = summaryItem.summaryTypes[0];
    if (isNil(primaryType) || isNil(summaryItem.getFormattedValueFor(primaryType))) {
      return undefined;
    }
    return this.unitFor(summaryItem, primaryType);
  }

  protected getPrimarySummaryLabel(summaryItem: SummaryItem<T>): string {
    const summaryTypes = summaryItem.summaryTypes;
    if (!summaryTypes || summaryTypes.length === 0) return '-';

    return this.summaryLabels[summaryTypes[0]];
  }

  protected getTooltipContent(summaryItem: SummaryItem<T>): string {
    const stats: string[] = [];
    const summaryTypes = summaryItem.summaryTypes;

    if (!summaryTypes || summaryTypes.length === 0) return '';

    // Always show all available statistics
    for (const type of EntitySummary.SUMMARY_TYPES) {
      if (summaryTypes.includes(type)) {
        const value = summaryItem.getFormattedValueFor(type);
        if (!isNil(value)) {
          const unit = this.unitFor(summaryItem, type);
          const shown = unit ? `${value} ${unit}` : value;
          stats.push(`<div><strong>${this.summaryLabels[type]}:</strong> ${shown}</div>`);
        }
      }
    }
    return stats.join('');
  }

  /** Count and distinct tally rows, so they are not in the column's unit. */
  private unitFor(
    summaryItem: SummaryItem<T>,
    type: EntitySummaryType
  ): string | undefined {
    return type === 'count' || type === 'distinct' ? undefined : summaryItem.column.unit;
  }

  protected readonly range = range;
}
